import { useState, forwardRef, Fragment, useMemo } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import HttpService from "../services/HttpService";
import { useQuery } from "@tanstack/react-query";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import MuiAlert from "@mui/material/Alert";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import OnboardingDetail from "../components/OnboardingDetail";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
// import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import BadgeIcon from "@mui/icons-material/Badge";
import RenderOnRole from "../components/RenderOnRole";
import CustomerActivityDialog from "../components/CustomerActivityDialog";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import { useChosenEmployer } from "../contexts/EmployerContext";

const STEP_NAMES = {
  2: "OTP Confirmed",
  3: "Employer Info",
  4: "Basic Info",
  5: "Main Address",
  6: "Mailing Address",
  7: "Terms Confirmed",
  8: "Finalized",
};

// Blue → teal → green gradient to convey progress through funnel
const FUNNEL_COLORS = [
  "#1565c0",
  "#0277bd",
  "#00838f",
  "#00695c",
  "#2e7d32",
  "#558b2f",
  "#f9a825",
  "#e65100",
];

export default function StartedOnboardingsRds() {
  const { chosenEmployerId } = useChosenEmployer();

  // Employer filter — only relevant for ANY-access users
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  // Step filter — set by clicking a funnel bar
  const [selectedStep, setSelectedStep] = useState(null);

  const columns = [
    { field: "id", headerName: "ID", width: 1, hide: true },

    {
      field: "companyId",
      headerName: "Company Id",
      width: 100,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.companyId}
          </Typography>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
      renderCell: (params) => {
        return (
          <Fragment>
            <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
              {params.row.email}
            </Typography>
          </Fragment>
        );
      },
    },
    {
      field: "step",
      headerName: "Step",
      width: 70,
      renderCell: (params) => {
        return (
          <Fragment>
            <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
              {params.row.step}
            </Typography>
          </Fragment>
        );
      },
    },
    {
      field: "cardType",
      headerName: "Card Type",
      width: 160,
      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "4rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
              {params.row.cardType}
            </Typography>
            <Typography
              sx={{ fontSize: 8, mb: 0, fontWeight: "bold", color: "gray" }}
              gutterBottom
            >
              {params.row.standaloneProduct ? "STANDALONE" : ""}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "action",
      width: 240,
      headerName: "Action",
      renderCell: (params) => {
        return (
          // you will find row info in params
          <div
            style={{
              backgroundColor:
                params.row.status === "REVIEW_IN_PROGRESS"
                  ? "antiquewhite"
                  : "white",
            }}
          >
            <Tooltip title={`Resend the email to ${params.row.email}`}>
              <Button
                style={{ minWidth: "10px" }}
                disabled={resendEmailOnboardingMutation.isLoading}
              >
                <ForwardToInboxIcon
                  onClick={(e) => onResendEmailButtonClick(e, params.row)}
                  variant="contained"
                />
              </Button>
            </Tooltip>

            {params.row.status ? (
              <Tooltip title="Click to edit">
                <Button style={{ minWidth: "10px" }}>
                  <ModeEditIcon
                    style={{ color: "darkgoldenrod" }}
                    onClick={(e) => onEditButtonClick(e, params.row)}
                    variant="contained"
                  />
                </Button>
              </Tooltip>
            ) : (
              <></>
            )}

            {params.row.status ? (
              <Button
                style={{ minWidth: "10px" }}
                disabled={confirmOnboardingMutation.isLoading}
                onClick={(e) => onConfirmButtonClick(e, params.row)}
              >
                <ThumbUpAltIcon
                  style={{ color: "green" }}
                  variant="contained"
                />
              </Button>
            ) : (
              <></>
            )}
            <Button style={{ minWidth: "10px" }}>
              <DeleteIcon
                style={{ color: "red" }}
                onClick={(e) => onDiscardButtonClick(e, params.row)}
                variant="contained"
              />
            </Button>

            <Tooltip title="View customer activity">
              <Button style={{ minWidth: "10px" }}>
                <TextSnippetIcon
                  style={{ color: "blue" }}
                  onClick={(e) => onCustomerActivityButtonClick(e, params.row)}
                  variant="contained"
                />
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
    { field: "duration", headerName: "Duration", width: 80 },

    {
      field: "creationDate",
      headerName: "Creation Date",
      width: 140,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.creationDate}
          </Typography>
        );
      },
    },
    {
      field: "updateDate",
      headerName: "Update Date",
      width: 140,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.updateDate}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.status ? params.row.status : "ON GOING"}
          </Typography>
        );
      },
    },
    {
      field: "reason",
      headerName: "Reason",
      width: 450,
      renderCell: (params) => {
        return (
          <Typography sx={{ fontSize: 12, mb: 0 }} gutterBottom>
            {params.row.reason}
          </Typography>
        );
      },
    },
  ];

  const queryClient = useQueryClient();

  const [customerIdentifier, setCustomerIdentifier] = useState();
  const [openEditDlg, setOpenEditDlg] = useState(false);

  // Activity dialog state
  const [activityDialog, setActivityDialog] = useState({
    open: false,
    loading: false,
    activities: [],
    totalCount: 0,
    pageSize: 25,
    hasMore: false,
    customerEmail: "",
    customerName: "",
  });

  const onEditButtonClick = (e, params) => {
    setCustomerIdentifier(params.email);
    setOpenEditDlg(true);
  };

  const handleSuccess = () => {
    refetch();
    setFeedback({
      open: true,
      type: "success",
      message: "The request was sent",
      onClose: () => {
        //clearFunction();
        setFeedback({ open: false });
        handleClose();
      },
    });
  };

  const discardOnboardingMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/discard`,
        { email: request.email }
      );
    },
    {
      onSuccess: () => {
        handleSuccess();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const onDiscardButtonClick = (e, params) => {
    discardOnboardingMutation.mutate({ email: params.email });
  };

  const onCustomerActivityButtonClick = async (e, params) => {
    try {
      if (!params?.email) {
        console.error("No email available for customer activity");
        return;
      }

      // Set loading state
      setActivityDialog((prev) => ({
        ...prev,
        loading: true,
        open: true,
        activities: [],
        customerEmail: params.email,
        customerName:
          `${params.firstName || ""} ${params.lastName || ""}`.trim() ||
          params.email,
      }));

      // Build URL - use email as the only parameter
      const url = `${window.API_URL}/member/activity?email=${encodeURIComponent(
        params.email
      )}&page=0&pageSize=50`;

      console.log("Fetching customer activity for email:", params.email);
      console.log("API URL:", url);

      // Make the actual API call
      const response = await HttpService.getAxiosClient().get(url);
      const data = response.data || {};

      console.log("Activity data:", data);

      // Update dialog with activities
      setActivityDialog({
        open: true,
        loading: false,
        activities: data.activities || [],
        customerEmail: params.email,
        customerName:
          `${params.firstName || ""} ${params.lastName || ""}`.trim() ||
          params.email,
        totalCount: data.totalCount || 0,
        page: data.page || 0,
        pageSize: data.pageSize || 50,
        hasMore: data.hasMore || false,
      });

      // Show feedback to user
      if (data.activities && data.activities.length > 0) {
        console.log(`Found ${data.activities.length} activity records`);
      } else {
        console.log("No activity records found");
      }
    } catch (error) {
      console.error("Error fetching customer activity:", error);

      // Update state to show error
      setActivityDialog({
        open: true,
        loading: false,
        activities: [],
        customerEmail: params?.email || "",
        customerName: params
          ? `${params.firstName || ""} ${params.lastName || ""}`.trim() ||
            params.email
          : "",
        totalCount: 0,
        page: 0,
        pageSize: 50,
        hasMore: false,
      });

      setFeedback({
        open: true,
        type: "error",
        message: "Error fetching member activity",
      });
    }
  };

  const removeOnboardingMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/remove`,
        { email: request.email }
      );
    },
    {
      onSuccess: () => {
        handleSuccess();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const onRemoveButtonClick = (e, params) => {
    removeOnboardingMutation.mutate({ email: params.email });
  };

  const confirmOnboardingMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/confirm`,
        { email: request.email, type: "rds" }
      );
    },
    {
      onSuccess: () => {
        handleSuccess();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err?.response?.data?.error
            ? err?.response?.data?.error
            : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const onConfirmButtonClick = (e, params) => {
    confirmOnboardingMutation.mutate({ email: params.email, type: "rds" });
  };

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });
  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return; //avoid closing the modal by clicking the backdrop
    //props.setOpen(false);
  };

  const resendEmailOnboardingMutation = useMutation(
    (request) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/onboarding/resend`,
        { email: request.email }
      );
    },
    {
      onSuccess: () => {
        handleSuccess();
      },
      onError: (err) => {
        const msg = err?.response?.data?.description
          ? err.response.data.description
          : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const onResendEmailButtonClick = (e, params) => {
    resendEmailOnboardingMutation.mutate({ email: params.email });
  };

  async function fetchOnboardingsStarted() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/onboarding/started?type=rds`
    );
    return response;
  }

  const { data, refetch, isLoading, isError } = useQuery(
    ["onboardings-started"],
    fetchOnboardingsStarted
  );

  // Fetch full employer list (name + id) — same pattern as Members page
  const { data: employersData, isLoading: isLoadingEmployers } = useQuery(
    ["employers"],
    () =>
      HttpService.getAxiosClient().get(
        window.API_URL + `/employer/all?excludeRelations=true`
      ),
    { staleTime: 5 * 60 * 1000, enabled: chosenEmployerId === "ANY" }
  );

  // Effective employer filter
  const effectiveCompanyId =
    chosenEmployerId !== "ANY"
      ? chosenEmployerId
      : selectedEmployer?.idEmployer || null;

  // Fetch funnel boundary counts: pre-authorized (campaign started) + employees (graduated)
  const { data: funnelStatsData } = useQuery(
    ["funnel-stats", effectiveCompanyId],
    () =>
      HttpService.getAxiosClient().get(
        window.API_URL +
          `/onboarding/funnel-stats${effectiveCompanyId ? `?companyId=${encodeURIComponent(effectiveCompanyId)}` : ""}`
      ),
    { staleTime: 5 * 60 * 1000 }
  );

  // Filtered rows (employer + optional step)
  const filteredRows = useMemo(() => {
    let rows = data?.data || [];
    if (effectiveCompanyId) {
      rows = rows.filter((r) => r.companyId === effectiveCompanyId);
    }
    if (selectedStep !== null) {
      rows = rows.filter((r) => String(r.step) === String(selectedStep));
    }
    return rows;
  }, [data, effectiveCompanyId, selectedStep]);

  // Funnel data — all steps 2–8 scaffolded; each embeds graduatedCount split when campaign exists
  const funnelData = useMemo(() => {
    const rows = effectiveCompanyId
      ? (data?.data || []).filter((r) => r.companyId === effectiveCompanyId)
      : data?.data || [];
    const counts = {};
    rows.forEach((r) => {
      const s = r.step != null ? String(r.step) : "Unknown";
      counts[s] = (counts[s] || 0) + 1;
    });

    const stats = funnelStatsData?.data;
    const showBoundaries =
      !!effectiveCompanyId && stats?.preauthorizedCount > 0;
    const campaignCount = showBoundaries ? stats.preauthorizedCount : null;
    const graduatedCount = showBoundaries ? stats.employeeCount || 0 : 0;

    // When campaign context exists, always show every step 2–8 (even if 0 in-progress)
    const ALL_STEPS = ["2", "3", "4", "5", "6", "7", "8"];
    const stepKeys = showBoundaries
      ? ALL_STEPS
      : Object.keys(counts).sort((a, b) => Number(a) - Number(b));

    const stepEntries = stepKeys.map((step, i) => {
      const count = counts[step] || 0;

      // "Past this step" = graduated employees + everyone at a later step
      // (they already moved through step N, so they count as having completed it)
      const laterStepsCount = showBoundaries
        ? stepKeys
            .filter((s) => Number(s) > Number(step))
            .reduce((sum, s) => sum + (counts[s] || 0), 0)
        : 0;
      const pastCount = graduatedCount + laterStepsCount;
      const totalForSizing = count + pastCount;

      return {
        step: STEP_NAMES[step]
          ? `${step} · ${STEP_NAMES[step]}`
          : `Step ${step}`,
        shortStep: STEP_NAMES[step] || `Step ${step}`,
        stepNum: step,
        count,
        graduatedCount: pastCount, // reuse field: green = "already past this step"
        totalForSizing,
        rawStep: step,
        pct:
          campaignCount != null && campaignCount > 0
            ? Math.round((count / campaignCount) * 100)
            : rows.length > 0
              ? Math.round((count / rows.length) * 100)
              : 0,
        color: FUNNEL_COLORS[i % FUNNEL_COLORS.length],
        isSpecial: false,
      };
    });

    const result = [];

    if (campaignCount != null) {
      result.push({
        step: "Campaign Started",
        shortStep: "Campaign Started",
        stepNum: "0",
        count: campaignCount,
        graduatedCount: 0,
        totalForSizing: campaignCount,
        rawStep: "CAMPAIGN",
        pct: 100,
        color: "#6a1b9a",
        isSpecial: true,
      });
    }

    result.push(...stepEntries);

    return result;
  }, [data, effectiveCompanyId, funnelStatsData]);

  // Show funnel only when employer is selected AND a campaign is in place
  const showFunnel =
    !!effectiveCompanyId && funnelData.some((d) => d.isSpecial);

  return (
    <Container maxWidth="xlg">
      <br />
      <br />
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={12}>
          {/* Header row: title + employer filter */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
              mb: 1,
            }}
          >
            <h1 style={{ margin: 0 }}>OnBoarding Applications</h1>

            {/* Only show dropdown for ANY-access users */}
            {chosenEmployerId === "ANY" && (
              <Box sx={{ minWidth: 320 }}>
                <Autocomplete
                  size="small"
                  options={employersData?.data || []}
                  getOptionLabel={(opt) =>
                    `${opt.name || ""} [${opt.idEmployer || ""}]`
                  }
                  isOptionEqualToValue={(opt, val) =>
                    opt.idEmployer === val.idEmployer
                  }
                  value={selectedEmployer}
                  onChange={(_, newValue) => {
                    setSelectedEmployer(newValue);
                    setSelectedStep(null);
                  }}
                  loading={isLoadingEmployers}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Employer"
                      placeholder="All employers"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingEmployers ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            )}
          </Box>

          {/* Funnel chart */}
          {!isLoading && !isError && funnelData.length > 0 && (
            <Accordion
              defaultExpanded={false}
              sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: "grey.50" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <FilterListIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {showFunnel
                      ? "Onboarding Funnel"
                      : "Pending Onboarding Distribution"}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${
                      (data?.data || []).filter(
                        (r) =>
                          !effectiveCompanyId ||
                          r.companyId === effectiveCompanyId
                      ).length
                    } in progress`}
                    variant="outlined"
                  />
                  {selectedStep !== null && (
                    <Chip
                      size="small"
                      label={`Filtered: ${STEP_NAMES[selectedStep] || `Step ${selectedStep}`}`}
                      onDelete={() => setSelectedStep(null)}
                      color="primary"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 3, pb: 3, bgcolor: "#fafafa" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Click a step to filter the table. Click again to clear.
                </Typography>

                {/* Color-key legend — shown only when funnel with campaign split */}
                {showFunnel &&
                  funnelData.some(
                    (d) => !d.isSpecial && d.graduatedCount > 0
                  ) && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2.5,
                        mb: 2,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            bgcolor: "#1b5e20",
                            borderRadius: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Completed / moved past this step
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            bgcolor: "#1565c0",
                            borderRadius: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Currently at step (clickable)
                        </Typography>
                      </Box>
                    </Box>
                  )}

                {!showFunnel
                  ? (() => {
                      // --- Pie chart: Pending Onboarding Distribution ---
                      const pieSlices = funnelData.filter(
                        (d) => !d.isSpecial && d.count > 0
                      );
                      const total = pieSlices.reduce((s, d) => s + d.count, 0);

                      if (pieSlices.length === 0) {
                        return (
                          <Typography color="text.secondary" sx={{ py: 2 }}>
                            No pending onboardings for this employer.
                          </Typography>
                        );
                      }

                      const CX = 150;
                      const CY = 150;
                      const R = 115;
                      const IR = 58;
                      let angle = -Math.PI / 2;
                      const slices = pieSlices.map((d) => {
                        const sa =
                          total > 0 ? (d.count / total) * 2 * Math.PI : 0;
                        const startA = angle;
                        angle += sa;
                        const endA = sa >= 2 * Math.PI ? angle - 0.0001 : angle;
                        const midA = startA + sa / 2;
                        const large = sa > Math.PI ? 1 : 0;
                        const x1 = CX + R * Math.cos(startA);
                        const y1 = CY + R * Math.sin(startA);
                        const x2 = CX + R * Math.cos(endA);
                        const y2 = CY + R * Math.sin(endA);
                        const ix1 = CX + IR * Math.cos(endA);
                        const iy1 = CY + IR * Math.sin(endA);
                        const ix2 = CX + IR * Math.cos(startA);
                        const iy2 = CY + IR * Math.sin(startA);
                        const path = [
                          `M ${x1.toFixed(1)} ${y1.toFixed(1)}`,
                          `A ${R} ${R} 0 ${large} 1 ${x2.toFixed(1)} ${y2.toFixed(1)}`,
                          `L ${ix1.toFixed(1)} ${iy1.toFixed(1)}`,
                          `A ${IR} ${IR} 0 ${large} 0 ${ix2.toFixed(1)} ${iy2.toFixed(1)}`,
                          "Z",
                        ].join(" ");
                        return { ...d, path, midA, sa };
                      });

                      return (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 4,
                            alignItems: "center",
                            justifyContent: "center",
                            flexWrap: "wrap",
                            py: 1,
                          }}
                        >
                          <svg width={300} height={300} viewBox="0 0 300 300">
                            {slices.map((s) => {
                              const isSelected = selectedStep === s.rawStep;
                              const isDimmed =
                                selectedStep !== null && !isSelected;
                              const tx = isSelected
                                ? (Math.cos(s.midA) * 8).toFixed(1)
                                : "0";
                              const ty = isSelected
                                ? (Math.sin(s.midA) * 8).toFixed(1)
                                : "0";
                              return (
                                <path
                                  key={s.rawStep}
                                  d={s.path}
                                  fill={isDimmed ? "#b0bec5" : s.color}
                                  opacity={isDimmed ? 0.5 : 0.9}
                                  stroke="white"
                                  strokeWidth="2"
                                  style={{
                                    cursor: "pointer",
                                    transform: `translate(${tx}px, ${ty}px)`,
                                    transition: "transform 0.15s",
                                  }}
                                  onClick={() =>
                                    setSelectedStep((prev) =>
                                      prev === s.rawStep ? null : s.rawStep
                                    )
                                  }
                                />
                              );
                            })}
                            <text
                              x={CX}
                              y={CY - 10}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fontSize="28"
                              fontWeight="bold"
                              fill="#333"
                            >
                              {total}
                            </text>
                            <text
                              x={CX}
                              y={CY + 16}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fontSize="12"
                              fill="#777"
                            >
                              pending
                            </text>
                          </svg>

                          {/* Legend */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                            }}
                          >
                            {slices.map((s) => {
                              const isSelected = selectedStep === s.rawStep;
                              const isDimmed =
                                selectedStep !== null && !isSelected;
                              return (
                                <Box
                                  key={s.rawStep}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    cursor: "pointer",
                                    opacity: isDimmed ? 0.4 : 1,
                                    borderRadius: 1,
                                    px: 0.5,
                                    py: 0.25,
                                    bgcolor: isSelected
                                      ? "action.selected"
                                      : "transparent",
                                  }}
                                  onClick={() =>
                                    setSelectedStep((prev) =>
                                      prev === s.rawStep ? null : s.rawStep
                                    )
                                  }
                                >
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      bgcolor: s.color,
                                      borderRadius: "50%",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ minWidth: 140 }}
                                  >
                                    Step {s.stepNum} · {s.shortStep}
                                  </Typography>
                                  <Typography variant="body2" fontWeight="bold">
                                    {s.count}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ({s.pct}%)
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    })()
                  : (() => {
                      // --- SVG trapezoid funnel ---
                      const GRAD_COLOR = "#1b5e20";
                      const allCounts = funnelData
                        .map((d) => d.totalForSizing ?? d.count)
                        .sort((a, b) => b - a);
                      const largest = allCounts[0] || 1;
                      const secondLargest = allCounts[1] || largest;
                      // Cap the scale so the largest bar is at most 20% wider than the second-largest
                      const displayMax =
                        largest > secondLargest * 1.2
                          ? secondLargest * 1.2
                          : largest;
                      const FULL_W = 414;
                      const MIN_W = 60;
                      const ROW_H = 40;
                      const GAP = 5;
                      const SVG_H = funnelData.length * (ROW_H + GAP) + GAP;
                      const SVG_W = FULL_W + 20;

                      const barW = (d) =>
                        Math.min(
                          FULL_W,
                          Math.max(
                            MIN_W,
                            Math.round(
                              ((d.totalForSizing ?? d.count) / displayMax) *
                                FULL_W
                            )
                          )
                        );

                      return (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 3,
                            alignItems: "flex-start",
                            justifyContent: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <Box
                            sx={{
                              overflowX: "auto",
                              flex: "0 0 auto",
                              width: 484,
                              maxWidth: "100%",
                            }}
                          >
                            <svg
                              width="100%"
                              viewBox={`0 0 ${SVG_W} ${SVG_H}`}
                              style={{
                                maxWidth: 484,
                                display: "block",
                              }}
                            >
                              {funnelData.map((d, i) => {
                                const w = barW(d);
                                const nextD = funnelData[i + 1];
                                const wNext = nextD ? barW(nextD) : w;

                                const y = i * (ROW_H + GAP) + GAP;
                                const cx = FULL_W / 2;

                                const x1Top = cx - w / 2;
                                const x2Top = cx + w / 2;
                                const x1Bot = cx - wNext / 2;
                                const x2Bot = cx + wNext / 2;

                                const isSelected =
                                  !d.isSpecial && selectedStep === d.rawStep;
                                const isDimmed =
                                  selectedStep !== null &&
                                  !d.isSpecial &&
                                  !isSelected;
                                const isClickable = !d.isSpecial && d.count > 0;

                                const fullPoints = [
                                  `${x1Top},${y}`,
                                  `${x2Top},${y}`,
                                  `${x2Bot},${y + ROW_H}`,
                                  `${x1Bot},${y + ROW_H}`,
                                ].join(" ");

                                // Split ratio: how much of the bar width is "graduated" (green)
                                const splitRatio =
                                  !d.isSpecial &&
                                  d.totalForSizing > 0 &&
                                  d.graduatedCount > 0
                                    ? d.graduatedCount / d.totalForSizing
                                    : 0;

                                const topSplit = x1Top + w * splitRatio;
                                const botSplit = x1Bot + wNext * splitRatio;

                                const gradPoints = [
                                  `${x1Top},${y}`,
                                  `${topSplit},${y}`,
                                  `${botSplit},${y + ROW_H}`,
                                  `${x1Bot},${y + ROW_H}`,
                                ].join(" ");

                                const stepPoints = [
                                  `${topSplit},${y}`,
                                  `${x2Top},${y}`,
                                  `${x2Bot},${y + ROW_H}`,
                                  `${botSplit},${y + ROW_H}`,
                                ].join(" ");

                                const gradCx = (x1Top + topSplit) / 2;
                                const stepCx = (topSplit + x2Top) / 2;
                                const gradW = topSplit - x1Top;
                                const stepW = x2Top - topSplit;

                                // Drop-off only between consecutive non-special steps
                                const nextStepD =
                                  !d.isSpecial && nextD && !nextD.isSpecial
                                    ? nextD
                                    : null;

                                return (
                                  <g
                                    key={d.rawStep}
                                    onClick={() =>
                                      isClickable &&
                                      setSelectedStep((prev) =>
                                        prev === d.rawStep ? null : d.rawStep
                                      )
                                    }
                                    style={{
                                      cursor: isClickable
                                        ? "pointer"
                                        : "default",
                                    }}
                                  >
                                    {/* Drop shadow for selected */}
                                    {isSelected && (
                                      <polygon
                                        points={fullPoints}
                                        fill="none"
                                        stroke={d.color}
                                        strokeWidth="3"
                                        transform="translate(0,2)"
                                        opacity="0.3"
                                      />
                                    )}

                                    {splitRatio > 0 ? (
                                      <>
                                        {/* Green graduated section */}
                                        <polygon
                                          points={gradPoints}
                                          fill={
                                            isDimmed ? "#c8e6c9" : GRAD_COLOR
                                          }
                                          opacity={isDimmed ? 0.7 : 0.88}
                                          stroke="white"
                                          strokeWidth="1"
                                        />
                                        {/* Step-color in-progress section (only if count > 0) */}
                                        {d.count > 0 && (
                                          <polygon
                                            points={stepPoints}
                                            fill={
                                              isDimmed ? "#b0bec5" : d.color
                                            }
                                            opacity={
                                              isDimmed
                                                ? 0.7
                                                : isSelected
                                                  ? 1
                                                  : 0.88
                                            }
                                            stroke="white"
                                            strokeWidth="1"
                                          />
                                        )}
                                      </>
                                    ) : (
                                      <polygon
                                        points={fullPoints}
                                        fill={isDimmed ? "#b0bec5" : d.color}
                                        opacity={
                                          isDimmed ? 0.7 : isSelected ? 1 : 0.88
                                        }
                                        stroke={isSelected ? d.color : "white"}
                                        strokeWidth={isSelected ? "2.5" : "1.5"}
                                        strokeDasharray={
                                          d.isSpecial ? "6 3" : undefined
                                        }
                                      />
                                    )}

                                    {/* Labels */}
                                    {splitRatio > 0 ? (
                                      <>
                                        {gradW > 28 &&
                                          (() => {
                                            const campaignBar = funnelData.find(
                                              (x) => x.rawStep === "CAMPAIGN"
                                            );
                                            const base = campaignBar
                                              ? campaignBar.count
                                              : d.totalForSizing;
                                            const gradPct =
                                              base > 0
                                                ? Math.round(
                                                    (d.graduatedCount / base) *
                                                      100
                                                  )
                                                : 0;
                                            return (
                                              <>
                                                <text
                                                  x={gradCx}
                                                  y={y + ROW_H / 2}
                                                  textAnchor="middle"
                                                  dominantBaseline="middle"
                                                  opacity={isDimmed ? 0.8 : 0.9}
                                                >
                                                  <tspan
                                                    fill={
                                                      isDimmed
                                                        ? "#4a6741"
                                                        : "white"
                                                    }
                                                    fontSize="18"
                                                    fontWeight="bold"
                                                  >
                                                    {d.graduatedCount}
                                                  </tspan>
                                                  <tspan
                                                    fill={
                                                      isDimmed
                                                        ? "#4a6741"
                                                        : "white"
                                                    }
                                                    fontSize="9"
                                                    opacity={
                                                      isDimmed ? 0.7 : 0.8
                                                    }
                                                    dx="3"
                                                  >
                                                    {gradPct}%
                                                  </tspan>
                                                </text>
                                              </>
                                            );
                                          })()}
                                        {/* In-progress labels: only show when count > 0 */}
                                        {d.count > 0 &&
                                          (stepW >= 52 ? (
                                            /* Wide enough: inline count + pct */
                                            <text
                                              x={stepCx}
                                              y={y + ROW_H / 2}
                                              textAnchor="middle"
                                              dominantBaseline="middle"
                                              opacity={isDimmed ? 0.8 : 1}
                                            >
                                              <tspan
                                                fill={
                                                  isDimmed ? "#37474f" : "white"
                                                }
                                                fontSize="20"
                                                fontWeight="bold"
                                              >
                                                {d.count}
                                              </tspan>
                                              <tspan
                                                fill={
                                                  isDimmed ? "#546e7a" : "white"
                                                }
                                                fontSize="11"
                                                opacity={isDimmed ? 0.8 : 0.85}
                                                dx="3"
                                              >
                                                {d.pct}%
                                              </tspan>
                                            </text>
                                          ) : (
                                            /* Narrow slice: compact inline label */
                                            <text
                                              x={topSplit + 4}
                                              y={y + ROW_H / 2}
                                              textAnchor="start"
                                              dominantBaseline="middle"
                                              fill={
                                                isDimmed ? "#37474f" : "white"
                                              }
                                              fontSize="8"
                                              fontWeight="bold"
                                              opacity={isDimmed ? 0.8 : 1}
                                            >
                                              {d.count} ({d.pct}%)
                                            </text>
                                          ))}
                                      </>
                                    ) : (
                                      <>
                                        <text
                                          x={cx}
                                          y={y + ROW_H / 2}
                                          textAnchor="middle"
                                          dominantBaseline="middle"
                                          opacity={isDimmed ? 0.8 : 1}
                                        >
                                          <tspan
                                            fill={
                                              isDimmed ? "#37474f" : "white"
                                            }
                                            fontSize="20"
                                            fontWeight="bold"
                                          >
                                            {d.count}
                                          </tspan>
                                          <tspan
                                            fill={
                                              isDimmed ? "#546e7a" : "white"
                                            }
                                            fontSize="11"
                                            opacity={isDimmed ? 0.8 : 0.9}
                                            dx="3"
                                          >
                                            {d.pct}%
                                          </tspan>
                                        </text>
                                      </>
                                    )}

                                    {/* Drop-off between consecutive normal steps */}
                                    {nextStepD && (
                                      <text
                                        x={cx}
                                        y={y + ROW_H + GAP / 2}
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        fill="#aaa"
                                        fontSize="9"
                                      >
                                        {nextStepD.count < d.count
                                          ? `▼ -${d.count - nextStepD.count}`
                                          : ""}
                                      </text>
                                    )}
                                  </g>
                                );
                              })}
                            </svg>
                          </Box>

                          {/* MUI legend — same design as pie, stacked vertically on right */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.75,
                              flex: "0 0 auto",
                              alignSelf: "center",
                            }}
                          >
                            {funnelData
                              .filter((d) => !d.isSpecial)
                              .map((d) => {
                                const isSelected = selectedStep === d.rawStep;
                                const isDimmedL =
                                  selectedStep !== null && !isSelected;
                                return (
                                  <Box
                                    key={d.rawStep}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      cursor:
                                        d.count > 0 ? "pointer" : "default",
                                      opacity: isDimmedL ? 0.35 : 1,
                                      borderRadius: 1,
                                      px: 1,
                                      py: 0.5,
                                      bgcolor: isSelected
                                        ? "action.selected"
                                        : "transparent",
                                      border: "1px solid",
                                      borderColor: isSelected
                                        ? d.color
                                        : "transparent",
                                    }}
                                    onClick={() =>
                                      d.count > 0 &&
                                      setSelectedStep((prev) =>
                                        prev === d.rawStep ? null : d.rawStep
                                      )
                                    }
                                  >
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        bgcolor: d.color,
                                        borderRadius: "50%",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{ minWidth: 110, color: "#444" }}
                                    >
                                      Step {d.stepNum} · {d.shortStep}
                                    </Typography>
                                    {d.count > 0 ? (
                                      <>
                                        <Typography
                                          variant="body2"
                                          fontWeight="bold"
                                          sx={{ color: d.color }}
                                        >
                                          {d.count}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          ({d.pct}%)
                                        </Typography>
                                      </>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        color="text.disabled"
                                      >
                                        —
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })}
                          </Box>
                        </Box>
                      );
                    })()}
              </AccordionDetails>
            </Accordion>
          )}

          {isLoading ? (
            <h4>Loading...</h4>
          ) : !isError ? (
            <div style={{ height: 700, width: "100%" }}>
              <DataGrid
                disableSelectionOnClick
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                rows={filteredRows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10]}
                components={{ Toolbar: GridToolbar }}
                componentsProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
              />
            </div>
          ) : (
            <h4>An error ocurred, please try again later...</h4>
          )}
        </Grid>
      </Grid>
      {openEditDlg ? (
        <OnboardingDetail
          action="SAVE"
          open={openEditDlg}
          setOpen={setOpenEditDlg}
          dialogTitle="Edit Onboarding"
          customerIdentifier={customerIdentifier}
          rds={true}
        />
      ) : (
        ""
      )}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>

      {/* Customer Activity Dialog */}
      <CustomerActivityDialog
        open={activityDialog.open}
        onClose={() => setActivityDialog({ ...activityDialog, open: false })}
        activityData={{
          activities: activityDialog.activities,
          totalCount: activityDialog.totalCount,
          pageSize: activityDialog.pageSize,
        }}
        loading={activityDialog.loading}
        customerName={activityDialog.customerName}
        customerEmail={activityDialog.customerEmail}
      />
    </Container>
  );
}
